import { lazy, type ReactNode, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { Spinner } from '@/components/common';
import { AppShell } from '@/components/layout/AppShell';
import { useApi } from '@/lib/api';
import { useLanguage } from '@/lib/i18n';
import { useSession } from '@/lib/session';
import { ApiExplorerPage } from '@/pages/ApiExplorerPage';
import { ConfirmEmailPage } from '@/pages/auth/ConfirmEmailPage';
import { InvitePage } from '@/pages/auth/InvitePage';
import { LoginPage, takeSsoNext } from '@/pages/auth/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage } from '@/pages/auth/PasswordPages';
import { TwoFactorRequiredPage } from '@/pages/auth/TwoFactorRequiredPage';
import { ConsentPage } from '@/pages/ConsentPage';
import { BulkSendPage } from '@/pages/documents/BulkSendPage';
import { DocumentPage } from '@/pages/documents/DocumentPage';
import { DocumentsPage } from '@/pages/documents/DocumentsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { SetupPage } from '@/pages/SetupPage';
import { AccountPage } from '@/pages/settings/AccountPage';
import { AdminSettingsPage } from '@/pages/settings/AdminSettingsPage';
import { ApiKeysPage } from '@/pages/settings/ApiKeysPage';
import { AuditPage } from '@/pages/settings/AuditPage';
import { AutomationRunsPage, AutomationsPage } from '@/pages/settings/AutomationsPage';
import { SettingsLayout } from '@/pages/settings/SettingsLayout';
import { SystemPage } from '@/pages/settings/SystemPage';
import { UsersPage } from '@/pages/settings/UsersPage';
import { WebhookDeliveriesPage } from '@/pages/settings/WebhookDeliveriesPage';
import { WebhooksPage } from '@/pages/settings/WebhooksPage';
import { EidContinuePage } from '@/pages/sign/EidContinuePage';
import { SharePage } from '@/pages/sign/SharePage';
import { SignPage } from '@/pages/sign/SignPage';
import { VerifyPage } from '@/pages/VerifyPage';

const DocsPage = lazy(() => import('@/pages/docs/DocsPage').then((m) => ({ default: m.DocsPage })));

function FullPageSpinner() {
  return (
    <div className="flex h-svh items-center justify-center">
      <Spinner className="size-6" />
    </div>
  );
}

/** Signed-in pages: unknown users go to sign in (or to setup on a fresh install), and back here afterwards. */
function RequireSession({ children }: { children: ReactNode }) {
  const { user, loading, can } = useSession();
  const setup = useApi('setup.status', undefined, { staleTime: 60_000 });
  const location = useLocation();
  const { language, setLanguage } = useLanguage();
  useEffect(() => {
    if (user?.language && user.language !== language) setLanguage(user.language);
  }, [user?.language, language, setLanguage]);
  const navigate = useNavigate();
  const signedIn = Boolean(user);
  useEffect(() => {
    const next = signedIn ? takeSsoNext() : null;
    if (next) navigate(next, { replace: true });
  }, [signedIn, navigate]);
  if (loading || setup.isPending) return <FullPageSpinner />;
  if (!user) {
    if (setup.data?.needsSetup) return <Navigate to="/setup" replace />;
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login${next === '/' ? '' : `?next=${encodeURIComponent(next)}`}`} replace />;
  }
  if (setup.data && !setup.data.emailConfigured && can('settings.manage')) return <Navigate to="/setup" replace />;
  if (user.mustEnableTwoFactor && (user.sso || !location.pathname.startsWith('/settings/account'))) return <TwoFactorRequiredPage />;
  return children;
}

export function App() {
  return (
    <Routes>
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/confirm-email" element={<ConfirmEmailPage />} />
      <Route path="/invite" element={<InvitePage />} />
      <Route path="/sign" element={<SignPage />} />
      <Route path="/sign/eid" element={<EidContinuePage />} />
      <Route path="/s/:slug" element={<SharePage />} />
      <Route path="/verify" element={<VerifyPage />} />
      <Route
        path="/api-explorer"
        element={
          <RequireSession>
            <ApiExplorerPage />
          </RequireSession>
        }
      />
      <Route
        path="/docs/*"
        element={
          <Suspense fallback={<FullPageSpinner />}>
            <DocsPage />
          </Suspense>
        }
      />
      <Route
        path="/oauth/consent"
        element={
          <RequireSession>
            <ConsentPage />
          </RequireSession>
        }
      />
      <Route
        element={
          <RequireSession>
            <AppShell />
          </RequireSession>
        }
      >
        <Route index element={<Navigate to="/documents" replace />} />
        <Route path="/documents" element={<DocumentsPage templates={false} />} />
        <Route path="/templates" element={<DocumentsPage templates />} />
        <Route path="/documents/:id" element={<DocumentPage />} />
        <Route path="/bulk/:id" element={<BulkSendPage />} />
        <Route path="/settings" element={<SettingsLayout />}>
          <Route index element={<Navigate to="/settings/account" replace />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="api-keys" element={<ApiKeysPage />} />
          <Route path="webhooks" element={<WebhooksPage />} />
          <Route path="webhooks/:id" element={<WebhookDeliveriesPage />} />
          <Route path="automations" element={<AutomationsPage />} />
          <Route path="automations/:id" element={<AutomationRunsPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="system" element={<SystemPage />} />
          <Route path="admin/:group" element={<AdminSettingsPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
